import React, { useState, useEffect } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Card,
    Typography,
    Button,
    Grid,
    Tooltip,
    Chip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import { useParams } from "react-router-dom";
import { apiGetDesafiosStats } from "../../../services/apiGetDesafiosStats";
import { Loader } from "../../../components/ui/Loader";

const getMonthName = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { month: 'long' });
};

export const DesafiosStatsTab = () => {
    const { id: comunidadeId } = useParams();
    const [desafiosStats, setDesafiosStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isUpdated, setIsUpdated] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'isActive', direction: "desc" });

    const fetchData = async () => {
        try {
            setLoading(true);
            setIsUpdated(false);

            const response = await apiGetDesafiosStats(comunidadeId);
            setDesafiosStats(response);
            setIsUpdated(true);
        } catch (error) {
            console.error("Erro ao buscar dados dos desafios:", error);
            setDesafiosStats(null);
            setIsUpdated(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [comunidadeId]);

    const handleSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    };

    const sortedData = React.useMemo(() => {
        if (!desafiosStats || !Array.isArray(desafiosStats)) return [];

        let sortedItems = [...desafiosStats];
        if (sortConfig.key) {
            sortedItems.sort((a, b) => {
                if (!a || !b) return 0;

                let valA = a[sortConfig.key];
                let valB = b[sortConfig.key];

                if (valA < valB) {
                    return sortConfig.direction === "asc" ? -1 : 1;
                }
                if (valA > valB) {
                    return sortConfig.direction === "asc" ? 1 : -1;
                }
                return 0;
            });
        }
        return sortedItems;
    }, [desafiosStats, sortConfig]);

    const generalStats = React.useMemo(() => {
        if (!desafiosStats || !Array.isArray(desafiosStats)) {
            return {
                totalDesafios: 0,
                activeDesafios: 0,
                totalCreatorsParticipated: 0,
                activeCreatorsParticipated: 0
            };
        }

        const activeDesafios = desafiosStats.filter(desafio => desafio.isActive);
        const uniqueCreators = new Set();
        const activeUniqueCreators = new Set();

        desafiosStats.forEach(desafio => {
            if (desafio.creators && Array.isArray(desafio.creators)) {
                desafio.creators.forEach(creator => {
                    uniqueCreators.add(creator.id || creator);
                    if (desafio.isActive) {
                        activeUniqueCreators.add(creator.id || creator);
                    }
                });
            } else if (desafio.uniqueCreators) {
                for (let i = 0; i < desafio.uniqueCreators; i++) {
                    uniqueCreators.add(`${desafio.id}_creator_${i}`);
                    if (desafio.isActive) {
                        activeUniqueCreators.add(`${desafio.id}_creator_${i}`);
                    }
                }
            }
        });

        return {
            totalDesafios: desafiosStats.length,
            activeDesafios: activeDesafios.length,
            totalCreatorsParticipated: uniqueCreators.size,
            activeCreatorsParticipated: activeUniqueCreators.size
        };
    }, [desafiosStats]);

    if (!isUpdated) {
        return <Loader />;
    }

    return (
        <Box pb={4}>
            {generalStats && (
                <Card sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Resumo Geral
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h4" color="primary">
                                    {generalStats.totalDesafios}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total de Desafios
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h4" color="primary">
                                    {generalStats.activeDesafios}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Desafios Ativos
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h4" color="primary">
                                    {generalStats.totalCreatorsParticipated}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total de Participantes
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ textAlign: "center" }}>
                                <Typography variant="h4" color="primary">
                                    {generalStats.activeCreatorsParticipated}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Participantes Ativos
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Card>
            )}

            <Card sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Typography variant="h6">Dados sobre Desafios</Typography>
                    <Tooltip title="Exportar para Excel">
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => exportToExcel(desafiosStats, sortedData)}
                            sx={{
                                color: "var(--primary-color)",
                                borderColor: "var(--primary-color)",
                                "&:hover": {
                                    borderColor: "var(--primary-color-dark)",
                                    backgroundColor: "rgba(var(--primary-color-rgb), 0.04)",
                                    transform: "scale(1.02)",
                                    transition: "all 0.2s",
                                },
                            }}
                        >
                            Exportar
                        </Button>
                    </Tooltip>
                </Box>

                <TableContainer component={Paper} sx={{ boxShadow: "none", overflow: "auto" }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell onClick={() => handleSort("titulo")} style={{ cursor: "pointer" }}>
                                    Título
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("isActive")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Status
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("dateEnd")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Mês
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("totalPosts")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Posts
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("uniqueCreators")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Participantes
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("avgStars")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Média Estrelas
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("totalComments")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Comentários
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("totalLikes")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Curtidas
                                </TableCell>
                                <TableCell
                                    align="center"
                                    onClick={() => handleSort("engagementRate")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Taxa Engajamento
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedData.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        {item.titulo}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        <Chip
                                            label={item.isActive ? "Ativo" : "Inativo"}
                                            color={item.isActive ? "success" : "default"}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        {getMonthName(item.dateEnd)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        {item.totalPosts}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        {item.uniqueCreators}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        <Chip
                                            label={item.avgStars ? item.avgStars.toFixed(1) : "0.0"}
                                            color={
                                                item.avgStars >= 2
                                                    ? "success"
                                                    : item.avgStars >= 1
                                                    ? "warning"
                                                    : "error"
                                            }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        {item.totalComments || 0}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        {item.totalLikes}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                                        <Chip
                                            label={`${item.engagementRate ? item.engagementRate.toFixed(1) : "0.0"}%`}
                                            color={
                                                item.engagementRate >= 70
                                                    ? "success"
                                                    : item.engagementRate >= 40
                                                    ? "warning"
                                                    : "error"
                                            }
                                            size="small"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {sortedData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        {loading 
                                            ? "Carregando..." 
                                            : generalStats?.totalDesafios === 0 
                                                ? "Nenhum desafio encontrado para esta comunidade"
                                                : "Nenhum dado encontrado"
                                        }
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </Box>
    );
};

const exportToExcel = (desafiosStats, sortedData) => {
    if (!desafiosStats || !Array.isArray(desafiosStats)) return;

    const headers = [
        "Título",
        "Status",
        "Mês",
        "Posts",
        "Participantes",
        "Média Estrelas",
        "Comentários",
        "Curtidas",
        "Taxa Engajamento (%)",
    ];

    const data = sortedData.map((item) => [
        item.titulo,
        item.isActive ? "Ativo" : "Inativo",
        getMonthName(item.dateEnd),
        item.totalPosts,
        item.uniqueCreators,
        item.avgStars ? item.avgStars.toFixed(2) : "0.00",
        item.totalComments,
        item.totalLikes,
        item.engagementRate ? item.engagementRate.toFixed(2) : "0.00",
    ]);

    const worksheetData = [headers, ...data];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Desafios");
    
    XLSX.writeFile(
        workbook,
        `desafios_stats_${new Date().toISOString().split("T")[0]}.xlsx`
    );
};
